import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAppSelector } from "../store/hooks";
import { selectAuth } from "../store/selectors";
import LoadingMask from "../components/LoadingMask";

interface Props {
  children: React.ReactNode;
}

export default function AuthGuard({ children }: Props) {
  const { isLoading, isAuth, user } = useAppSelector(selectAuth);
  const location = useLocation();
  const path = location.pathname;

  if (isLoading) {
    return <LoadingMask isLoading={isLoading} />;
  }

  // Not authenticated - allow public pages only
  if (!isAuth) {
    if (
      path !== "/login" &&
      path !== "/signup" &&
      path !== "/forgot-password" &&
      path !== "/change-password" &&
      path !== "/verify" &&
      path !== "/resend-verify" &&
      path !== "/check-verify"
    ) {
      return <Navigate to="/login" state={{ from: location }} replace />;
    }
    return <>{children}</>;
  }

  // Authenticated but no user data - show loading
  if (isAuth && !user) {
    return <LoadingMask isLoading={true} />;
  }

  // Authenticated and verified - allow all except auth pages
  if (isAuth && user.isVerified) {
    if (path === "/login" || path === "/signup" || path === "/resend-verify") {
      return <Navigate to="/" replace />;
    }
    return <>{children}</>;
  }

  // Authenticated but not verified - only allow verification pages
  if (isAuth && !user.isVerified) {
    if (path === "/resend-verify" || path === "/check-verify") {
      return <>{children}</>;
    }
    return <Navigate to="/resend-verify" replace />;
  }

  return <Navigate to="/login" replace />;
}
